import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getStudentByToken,
  putStudents,
  getCacheSize,
} from 'utils/scannerDB';
import { getStudentsCache } from 'services/qrService';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 min
const STORAGE_KEY          = 'qr-cache-last-sync';

const getLastSync = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? parseInt(raw, 10) : 0;
};

const saveLastSync = () => {
  localStorage.setItem(STORAGE_KEY, Date.now().toString());
};

const isCacheStale = () => Date.now() - getLastSync() > REFRESH_INTERVAL_MS;

const useStudentsCache = () => {
  const [cacheSize, setCacheSize]   = useState(0);
  const [lastSync, setLastSync]     = useState(getLastSync());
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const timerRef                    = useRef(null);

  // Descarga todas las páginas del cache desde datofy-api
  const fetchAllPages = useCallback(async () => {
    let page     = 1;
    let lastPage = 1;
    const all    = [];

    do {
      const res = await getStudentsCache(page, 200);
      if (!res.success) break;

      all.push(...(res.data ?? []));
      lastPage = res.meta?.last_page ?? 1;
      page++;
    } while (page <= lastPage);

    return all;
  }, []);

  const refreshCache = useCallback(async (force = false) => {
    if (!force && !isCacheStale()) return;
    setLoading(true);
    setError(null);

    try {
      const students = await fetchAllPages();

      if (students.length > 0) {
        await putStudents(students);
      }
      // Guardar sync aunque esté vacío para no re-intentar cada carga
      saveLastSync();
      const size = await getCacheSize();
      setCacheSize(size);
      setLastSync(Date.now());
    } catch (e) {
      setError(e?.response?.data?.message ?? 'No se pudo actualizar el cache. Verifica migraciones y servidor.');
      // No bloqueamos — el cache anterior sigue disponible
    } finally {
      setLoading(false);
    }
  }, [fetchAllPages]);

  // Al montar: leer tamaño del cache existente + refrescar si está desactualizado
  useEffect(() => {
    getCacheSize().then(setCacheSize);
    refreshCache();

    // Refresco periódico en background
    timerRef.current = setInterval(() => refreshCache(), REFRESH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [refreshCache]);

  // Lookup offline por token UUID
  const lookupByToken = useCallback(async (token) => {
    return getStudentByToken(token);
  }, []);

  const lastSyncDate = lastSync
    ? new Date(lastSync).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    : null;

  return { cacheSize, lastSyncDate, loading, error, lookupByToken, refreshCache };
};

export default useStudentsCache;
